import React, { useState, useRef, useEffect } from "react";
import Checkbox from "../Checkbox";
import Select from "../Select";
import useOutsideClick from "../../hooks/useOutsideClick";

export const MultiSelect = ({
  id,
  onChange,
  options = [],
  selectedValues,
  children,
  value,
  ...props
}) => {
  const [selectValue, setSelectValue] = useState({
    label: "Select an option",
    value: "",
  });
  const [selected, setSelected] = useState([]);
  const [dropOpen, setDropOpen] = useState(false);
  const ref = useRef();

  // Sync with external value prop if provided
  useEffect(() => {
    if (value !== undefined && Array.isArray(value)) {
      const selectedOptions = options.filter((opt) =>
        value.includes(opt.value)
      );
      setSelected(selectedOptions);
    } else if (
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    ) {
      // Clear selection if value is empty array or undefined
      setSelected([]);
    }
  }, [value, options]);

  useOutsideClick(ref, () => {
    setDropOpen(false);
  });

  function handleChange(e) {
    // `Select` emits the rendered option object (whose `label` is often a React element).
    // We want to keep a plain-text label in `selected` so the summary doesn't show `[object Object]`.
    const baseOption = options.find((o) => o.value === e.value);
    const newValue = {
      value: e.value,
      id: e.id,
      label: baseOption?.label ?? e.label ?? "",
    };
    const checked = selected.findIndex((v) => {
      return v.value === e.value;
    });

    if (checked >= 0) {
      const newArray = selected.filter((item) => item.value !== e.value);
      setSelected(newArray);
      onChange && onChange(newArray);
      if (newArray.length === 0) {
        onChange && onChange([]);
        setSelectValue({ label: "Select an option", value: "" });
      }
      setSelectValue({ label: getSummaryLabel(newArray), value: "" });
    } else {
      const newSelected = [...selected, newValue];
      setSelected(newSelected);
      onChange && onChange(newSelected);
      setSelectValue({ label: getSummaryLabel(newSelected), value: "" });
    }
    // Don't close the dropdown - keep it open for multiple selections
    setDropOpen(true);
  }

  function renderOptions() {
    return options.map((item, i) => {
      const checked = selected.filter((v) => v.value === item.value).length > 0;
      const labelText =
        typeof item.label === "string"
          ? item.label
          : item.fullName || item.name || item.label || "";

      return {
        ...item, // Preserve all original properties for search (including fullName, firstName, etc.)
        label: labelText, // Keep original label as string for search
        // Use body for JSX rendering
        body: (
          <div className="flex items-center space-x-2">
            <Checkbox
              checked={checked}
              name={labelText}
              sizeclassName="w-4 h-4"
            />
            <span>{labelText}</span>
          </div>
        ),
        value: item.value,
        id: item.id,
      };
    });
  }

  const label = selected
    .map((item) => item.label)
    .filter((item) => item !== "");

  // When search is enabled and nothing is selected, use empty string so placeholder shows
  // Otherwise show the selected count or names
  const displayLabel =
    label.length === 0
      ? props.search
        ? ""
        : props.placeholder || "Select employees"
      : label.length === 1
      ? label[0]
      : `${label.length} ${(props.selectionNoun || "employees").trim()} selected`;

  return (
    <div className="w-full" ref={ref}>
      <Select
        id={id}
        onChange={handleChange}
        options={renderOptions()}
        value={displayLabel}
        keepOpen={true}
        search={props.search}
        searchKeys={props.searchKeys}
        placeholder={props.placeholder || "Search or select employees"}
        label={props.label ? props.label : null}
      />
      {children &&
        React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              selected,
              onChange: handleChange,
            });
          }
          return child;
        })}
    </div>
  );
};

export default MultiSelect;
